import {Component, OnInit, ViewChild} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular';
import {ColorSchemesService} from './color-schemes.service';
import {ColorSchemeDataModel} from '../../../models/configuration/color-scheme-data.model';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {AbstractControl, UntypedFormControl, Validators} from '@angular/forms';
import {TdoColorPickerEvent} from '../../../tdo-controls/common/tdo-color-picker/models/tdo-color-picker.event';
import {DialogService} from '../../../services/dialog.service';
import {TdoTableSelectionEvent} from '../../../tdo-controls/common/tdo-table-v2/models/tdo-table-selection.event';
import {TdoTableV2Component} from '../../../tdo-controls/common/tdo-table-v2/tdo-table-v2.component';
import {ConfigurationService} from '../configuration.service';

@Component({
  selector: 'app-color-schemes',
  templateUrl: './color-schemes.component.html',
  styleUrls: ['./color-schemes.component.css']
})

export class ColorSchemesComponent implements OnInit {
  @ViewChild('grid') dataGrid: DxDataGridComponent;
  @ViewChild('schemeTable') schemeTable: TdoTableV2Component;

  currentBackground = '#f05b41';
  currentForeground = '#5b41f0';
  colorSchemeList: Array<ColorSchemeDataModel>;
  isNewScheme: boolean;

  schemeNameFormControl: UntypedFormControl;
  errorMessage: string = '';

  defaultColor = '#ffffff';

  // This is used by setter / getter
  private _selectedColorScheme: ColorSchemeDataModel = null;

  constructor(private colorSchemesService: ColorSchemesService,
              private dialogService: DialogService,
              private snackbar: MatSnackBar,
              public configurationService: ConfigurationService,
              private dialog: MatDialog) {
    this.schemeNameFormControl = new UntypedFormControl('', [Validators.required, Validators.minLength(1), this.nameValidator]);
    this.schemeNameFormControl.valueChanges.subscribe(value => this.selectedColorScheme.ColorSchemeName = value);
  }

  ngOnInit() {
    this.getColorSchemesData();
  }

  getColorSchemesData() {
    this.colorSchemesService.getColorSchemes(true)
      .subscribe((response) => {
        this.colorSchemeList = response;
        if (this.colorSchemeList.length > 0) {
          this.selectedColorScheme = response[0];
          setTimeout(() => {
            this.schemeTable.selectionControl.select(this.selectedColorScheme);
          }, 500);

        }
        // this.schemeTable.updateData(this.colorSchemeList);
      });
  }

  onNew(e) {
    this.configurationService.setDirty();
    this.isNewScheme = true;
    const newColorScheme = new ColorSchemeDataModel();
    newColorScheme.ColorSchemeId = '00000000-0000-0000-0000-000000000000';
    newColorScheme.ColorSchemeName = 'New Scheme';
    if (this.selectedColorScheme) {
      newColorScheme.ForeColor = '#ffffff';
      newColorScheme.BackColor = '#ff0000';
    }
    /*this.colorSchemeList.push(newColorScheme);
    this.refreshTableData(this.colorSchemeList);*/
    this.selectedColorScheme = newColorScheme;
  }

  onForegroundColorChange(event: TdoColorPickerEvent) {
    this.configurationService.setDirty();
    this.selectedColorScheme.ForeColor = event.hex;
  }

  onBackgroundColorChange(event: TdoColorPickerEvent) {
    this.configurationService.setDirty();
    this.selectedColorScheme.BackColor = event.hex;
  }

  nameValidator(control: AbstractControl): {[key: string]: any} | null {
    if (control.value !== 'New Scheme Name') {
      return null;
    } else {
      return {invalidName: {valid: false, value: control.value}};
    }
  }

  onSave(e) {
    if (!this.schemeNameFormControl.valid) {
      if (this.schemeNameFormControl.errors) {
        this.errorMessage = 'Please give the scheme a valid name.';
      }
      this.schemeNameFormControl.markAllAsTouched();
      this.schemeNameFormControl.updateValueAndValidity();
      return;
    }

    this.errorMessage = '';
    this.colorSchemesService.saveColorScheme(this._selectedColorScheme, this.isNewScheme)
      .subscribe(postResponse => {
        this.configurationService.setClean();
        if (this.isNewScheme) {
          this.colorSchemeList.push(this.selectedColorScheme);
          this.isNewScheme = false;
        }
        this.snackbar.open('Scheme save success', '', {duration: 5000});
        this.schemeTable.updateData(this.colorSchemeList);
      });
  }

  removeColorScheme(model: ColorSchemeDataModel) {
    this.colorSchemesService.deleteColorScheme(model.ColorSchemeId)
      .subscribe(response => {
        const idx = this.colorSchemeList.findIndex(p => p.ColorSchemeId === model.ColorSchemeId);
        if (idx > -1) {
          let selectionId = 0;
          if (idx < this.colorSchemeList.length - 1) {
            selectionId = idx + 1;
          } else if (idx) {
            selectionId = idx - 1;
          }

          this.colorSchemeList.splice(idx, 1);
          this.schemeTable.updateData(this.colorSchemeList);
          if (this.colorSchemeList.length > 0) {
            this.selectedColorScheme = this.colorSchemeList[selectionId];
          } else {
            this.selectedColorScheme = null;
          }
          this.snackbar.open('Scheme successfully deleted.', '', {duration: 5000});
        }
      }, error => {
        this.snackbar.open('Scheme deletion failed.', '', {duration: 5000});
      });
  }

  deleteScheme(model: ColorSchemeDataModel) {
    this.dialogService.openConfirmDialog(`Are you sure you want to delete ${model.ColorSchemeName}?`).subscribe((result) => {
      if (result === true) {
        this.removeColorScheme(model);
      } else {
        //
      }
    });
  }

  get selectedColorScheme() {
    return this._selectedColorScheme;
  }

  set selectedColorScheme(value: ColorSchemeDataModel) {
    this._selectedColorScheme = value;
    this.schemeNameFormControl.setValue(this._selectedColorScheme.ColorSchemeName);
  }

  get foregroundColor() {
    if (this.selectedColorScheme.ForeColor) {
      return this.selectedColorScheme.ForeColor;
    } else {
      return this.defaultColor;
    }
  }

  get backgroundColor() {
    if (this.selectedColorScheme.BackColor) {
      return this.selectedColorScheme.BackColor;
    } else {
      return this.defaultColor;
    }
  }

  onSchemeChange(event: TdoTableSelectionEvent) {
    const l = this.configurationService.checkIsDirty();
    if (l) {
      l.subscribe((res) => {
        if (res) {
          this.selectedColorScheme = event.model;
          return;
        } else {
          this.schemeTable.selectionControl.select(this.selectedColorScheme);
        }
      });
    } else {
      this.selectedColorScheme = event.model;
    }
  }
}
